import React, {
    PropsWithChildren,
    createContext,
    useContext,
    useState,
    useEffect,
    MutableRefObject,
    useRef,
    useCallback,
    useMemo,
    Dispatch,
    SetStateAction,
} from 'react';
import {useAppDispatch, useAppSelector, store} from 'store/customer';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {setBackupProductValues} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {selectProductUpdated} from 'components/customer/Product/store/productSlice';
import {useParams, useSearchParams} from 'react-router-dom';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useDialogContext} from 'contexts/DialogContext';
import {
    registerRoomPlannerNotify,
    unregisterRoomPlannerNotify,
} from 'components/customer/RoomPlanner/lib/useRoomPlannerNotification';
import {useGetProductDefaultQuery} from 'components/customer/Product/helpers/useGetProductDefaultQuery';
import {
    useGetRoomProductsQuery,
    useLazyGetCabinetDetailsQuery,
    useLazyGetProductDefaultQuery,
    useLazyGetProductStructureQuery,
    useLazyGetQFPProductStructureQuery,
} from 'components/customer/Product/store/productApi';
import {Product} from 'components/customer/Product/entity/Product';
import {useQueryParams} from 'shared/helpers';
import {useLazyGetProductDataQuery} from 'components/customer/Product/store/useProductApi';
import {confirmPlaceholderDelete} from 'components/customer/RoomPlanner/helpers/placeholder/confirmDelete';
import {useGetRoomPreviewQuery} from 'components/customer/Preview3D/store/previewApi';

// Add state for active direction and position
export type Direction = 'right' | 'left' | 'back' | 'front' | 'up' | 'down';

type EditorMode = 'distance' | 'movement' | null;

interface RoomPlannerContext {
    selectedProduct?: Product | null;
    selectedCabinetGroup?: CabinetMesh | null;
    setSelectedCabinetGroup?: React.Dispatch<
        React.SetStateAction<CabinetMesh | null>
    >;
    cabinetGroupListRef?: MutableRefObject<CabinetMesh[]>;
    placeholderListRef?: MutableRefObject<CabinetMesh[]>;
    setSelectedProduct?: (productId: number | string) => void;
    originalDepthRef?: MutableRefObject<number>;
    originalHeightRef?: MutableRefObject<number>;
    originalWidthRef?: MutableRefObject<number>;
    deletedCabinetsRef?: MutableRefObject<Map<number, CabinetMesh>>;

    setEditModeHandler?: (handler: (() => void) | null) => void;
    setDuplicateHandler?: (handler: (() => void) | null) => void;
    setDoorToggleHandler?: (handler: (() => void) | null) => void;
    setRotateRightHandler?: (handler: (() => void) | null) => void;
    setMovementHandler?: (handler: (() => void) | null) => void;
    setDeleteHandler?: (handler: (() => void) | null) => void;
    setUndoHandler?: (handler: () => void | Promise<void>) => void;
    setRedoHandler?: (
        handler: (
            skipHistoryIndexUpdate?: boolean,
            targetIndex?: number
        ) => void | Promise<void>
    ) => void;
    setGoToHistoryIndex?: (handler: (targetIndex: number) => void) => void;
    setCloseProductEditorMenu?: (
        handler: (yesHandler?: () => void) => void
    ) => void;
    hasUnsavedProductChanges?: boolean;
    confirmDiscardProductChanges?: (yesHandler?: () => void) => void;
    undoHandler?: () => void;
    redoHandler?: (
        skipHistoryIndexUpdate?: boolean,
        targetIndex?: number
    ) => void;
    goToHistoryIndex?: (targetIndex: number) => void;
    deleteHandler?: () => void;
    activeDirection?: Direction;
    setActiveDirection?: Dispatch<SetStateAction<Direction>>;
    editorMode?: EditorMode;
    setEditorMode?: Dispatch<SetStateAction<EditorMode>>;
    isRoomPreview?: boolean;
}

const RoomPlannerContext = createContext<RoomPlannerContext>({});

const useRoomPlannerContext = () => useContext(RoomPlannerContext);

interface RoomPlannerProviderProps {
    isRoomPreview?: boolean;
}

const RoomPlannerProvider = ({
    children,
    isRoomPreview = false,
}: PropsWithChildren<RoomPlannerProviderProps>) => {
    const dispatch = useAppDispatch();
    const {notify} = useNotificationContext();
    const {showDialog} = useDialogContext();
    const {fetchProductDefaultValues} = useGetProductDefaultQuery();
    const [getProductConfig] = useLazyGetQFPProductStructureQuery();
    const [getProductData] = useLazyGetProductDataQuery();
    const [getCabinetDetails] = useLazyGetCabinetDetailsQuery();
    const [getProductDefaultValues] = useLazyGetProductDefaultQuery();
    const [getProductStructure] = useLazyGetProductStructureQuery();

    const {roomId: paramRoomId, roomSlug = ''} = useParams();
    const {data: roomPreviewData} = useGetRoomPreviewQuery(
        {slug: roomSlug},
        {
            skip: !isRoomPreview || !roomSlug,
        }
    );
    const roomId = paramRoomId ?? roomPreviewData?.room.id;
    const [selectedCabinetGroup, setSelectedCabinetGroup] =
        useState<CabinetMesh | null>(null);

    const {data: {products: roomProducts} = {}} = useGetRoomProductsQuery(
        {roomId: Number(roomId)},
        {skip: !roomId || isRoomPreview}
    );
    const productData = isRoomPreview
        ? roomPreviewData?.job_cabinets
        : roomProducts;

    const cabinetId =
        selectedCabinetGroup?.userData?.values?.job_cabinet_id?.toString();

    const selectedProduct = useMemo(() => {
        if (!cabinetId || !productData) return null;
        return (
            productData.find(
                (p) => p.id !== undefined && p.id.toString() === cabinetId
            ) || null
        );
    }, [cabinetId, productData]);

    const [activeDirection, setActiveDirection] = useState<Direction>(null);
    // State for editor mode (movement arrows or distance measurements)
    const [editorMode, setEditorMode] = useState<EditorMode>(null);

    const cabinetGroupListRef = useRef<CabinetMesh[]>([]);
    const placeholderListRef = useRef<CabinetMesh[]>([]);
    const originalDepthRef = useRef<number>(null);
    const originalHeightRef = useRef<number>(null);
    const originalWidthRef = useRef<number>(null);
    const deletedCabinetsRef = useRef<Map<number, CabinetMesh>>(new Map());
    const editModeHandlerRef = useRef<(() => void) | null>(null);
    const duplicateHandlerRef = useRef<(() => void) | null>(null);
    const doorToggleHandlerRef = useRef<(() => void) | null>(null);
    const rotateRightHandlerRef = useRef<(() => void) | null>(null);
    const movementHandlerRef = useRef<(() => void) | null>(null);
    const deleteHandlerRef = useRef<(() => void) | null>(null);
    const undoHandlerRef = useRef<(() => void) | null>(null);
    const redoHandlerRef = useRef<
        | ((
              skipHistoryIndexUpdate?: boolean,
              originIndex?: number,
              targetIndex?: number
          ) => void)
        | null
    >(null);
    const goToHistoryIndexRef = useRef<((targetIndex: number) => void) | null>(
        null
    );
    const previousSelectedRef = useRef<CabinetMesh | null>(null);
    const closeProductEditorMenuRef = useRef<
        ((yesHandler?: () => void) => void) | null
    >(null);
    const [searchParams] = useSearchParams();

    const productUpdated = useAppSelector(selectProductUpdated);
    const hasUnsavedProductChanges =
        searchParams.has('cabinetId') && productUpdated;

    const setSelectedProduct = (productId: number | string) => {
        const mesh = cabinetGroupListRef.current.find(
            (mesh) =>
                String(mesh.userData.values.id) === String(productId) ||
                String(mesh.userData.values.job_cabinet_id) ===
                    String(productId) ||
                String(mesh.userData.id) === String(productId) ||
                String(mesh.userData.id).startsWith(`${productId}-`)
        );

        if (mesh) {
            dispatch(setBackupProductValues(mesh.userData.values));
            // highlightCabinet syncs React selection state and applies the
            // same visual outline used by click selection (needed for hash
            // deep-links on room preview load).
            productEvents.highlightCabinet(mesh);
        }
    };

    const setEditModeHandler = useCallback((handler: (() => void) | null) => {
        editModeHandlerRef.current = handler;
    }, []);

    const setDuplicateHandler = useCallback((handler: (() => void) | null) => {
        duplicateHandlerRef.current = handler;
    }, []);

    const setDoorToggleHandler = useCallback((handler: (() => void) | null) => {
        doorToggleHandlerRef.current = handler;
    }, []);

    const setRotateRightHandler = useCallback(
        (handler: (() => void) | null) => {
            rotateRightHandlerRef.current = handler;
        },
        []
    );

    const setMovementHandler = useCallback((handler: (() => void) | null) => {
        movementHandlerRef.current = handler;
    }, []);

    const setDeleteHandler = useCallback((handler: (() => void) | null) => {
        deleteHandlerRef.current = handler;
    }, []);

    const setUndoHandler = useCallback((handler: () => void) => {
        undoHandlerRef.current = handler;
    }, []);

    const setRedoHandler = useCallback(
        (
            handler: (
                skipHistoryIndexUpdate?: boolean,
                originIndex?: number,
                targetIndex?: number
            ) => void
        ) => {
            redoHandlerRef.current = handler;
        },
        []
    );

    const setGoToHistoryIndex = useCallback(
        (handler: (targetIndex: number) => void) => {
            goToHistoryIndexRef.current = handler;
        },
        []
    );

    const setCloseProductEditorMenu = useCallback(
        (handler: (yesHandler?: () => void) => void) => {
            closeProductEditorMenuRef.current = handler;
        },
        []
    );

    useEffect(() => {
        productEvents.setSetSelectedCabinetGroup(setSelectedCabinetGroup);
    }, [setSelectedCabinetGroup]);

    useEffect(() => {
        registerRoomPlannerNotify(notify);
        return () => unregisterRoomPlannerNotify();
    }, [notify]);

    const {product: productIdQuery} = useQueryParams() as {product?: string};
    const prevProductRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (isRoomPreview || !selectedCabinetGroup) return;

        // Placeholders are not job cabinets — skip product/structure/details APIs
        // (otherwise values.id/type are undefined → NaN requests).
        if (selectedCabinetGroup.userData?.isPlaceholder) return;

        const cabinetId = String(
            selectedCabinetGroup.userData?.values?.id ?? ''
        );
        const productId = String(
            selectedCabinetGroup.userData?.values?.type ?? ''
        );

        if (
            !productId ||
            !cabinetId ||
            !roomId ||
            productId === 'undefined' ||
            cabinetId === 'undefined' ||
            Number.isNaN(Number(productId)) ||
            Number.isNaN(Number(cabinetId))
        ) {
            return;
        }

        if (
            (prevProductRef.current === undefined ||
                prevProductRef.current === null) &&
            productIdQuery != null
        ) {
            prevProductRef.current = productIdQuery;
            return; // skip first appearance
        }

        prevProductRef.current = productIdQuery ?? undefined;

        if (
            productIdQuery !== undefined &&
            productIdQuery !== null &&
            productIdQuery !== prevProductRef.current
        ) {
            prevProductRef.current = productIdQuery;
            // continue to fetch logic
        }

        let isCancelled = false;

        const fetchSelectionData = async () => {
            try {
                await Promise.all([
                    fetchProductDefaultValues(Number(productId)),
                    getProductConfig({
                        cabinetType: parseInt(productId),
                        qfp: false,
                    }),
                    getProductData({
                        cabinetId: Number(cabinetId),
                        cabinetType: Number(productId),
                        roomId: Number(roomId),
                    }),
                    getCabinetDetails({cabinetId: Number(productId)}),
                    getProductDefaultValues({cabinetType: Number(productId)}),
                    getProductStructure({cabinetType: Number(productId)}),
                ]);

                if (!isCancelled) {
                }
            } catch (err) {
                if (!isCancelled) {
                }
            }
        };

        void fetchSelectionData();

        return () => {
            isCancelled = true;
        };
    }, [isRoomPreview, selectedCabinetGroup, roomId, productIdQuery]);

    // Add keyboard event listener for delete key, undo (Ctrl+Z), and redo (Ctrl+Y)
    // This allows users to press the Delete key to delete selected cabinets
    // or placeholders, and use Ctrl+Z/Ctrl+Y for undo/redo operations
    useEffect(() => {
        const previousSelected = previousSelectedRef.current;
        previousSelectedRef.current = selectedCabinetGroup ?? null;

        /*
         * Only clean up when a mesh was selected and now is not. This
         * effect also re-runs when the URL `selected` param lands, which
         * can happen a frame before React stores the mesh. Dropping the
         * params in that gap cleared a brand-new click — dotted line
         * stayed, blue highlight flickered off, cabinet was not selected.
         */
        if (selectedCabinetGroup || !previousSelected) {
            return;
        }

        const currentSelectedCabinet =
            previousSelected.parent != null
                ? previousSelected
                : productEvents['selectedCabinet'];

        // Skip meshes already removed from the scene (e.g. Delete on a
        // placeholder) — traverse would walk disposed geometry.
        if (currentSelectedCabinet?.parent) {
            // Cabinets and placeholders share the same outline handlers, so
            // resetSegmentColor clears both. Do not call onCabinetSelect(false)
            // here: it paints the outline blue between this reset and the
            // async revalidateOverlaps pass — the deselect flicker.
            currentSelectedCabinet.traverse((child) => {
                const userData = child.userData as {
                    resetSegmentColor?: (reset: boolean) => void;
                };
                if (typeof userData?.resetSegmentColor === 'function') {
                    userData.resetSegmentColor(false);
                }
            });
        }

        /*
         * Only the live 3D copy is cleared here. Writing the URL from this
         * effect raced editor close/cancel: a stale snapshot put
         * placeholderEdit/cabinetId back, so the first click deselected
         * and the second click closed the editor.
         */
        productEvents.dropSelectionParams();
        productEvents.clearSelectedCabinet();

        // Re-apply overlap red AFTER deselect clears the outline to black.
        // Without this, overlapping cabinets stay black until another
        // floor click re-runs revalidateOverlaps.
        productEvents.revalidateOverlaps();
    }, [selectedCabinetGroup]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check that the user is not typing in an input field
            const isInputField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(
                (event.target as HTMLElement)?.tagName
            );

            if (isInputField) {
                return;
            }

            const isNotMovementMode =
                !selectedCabinetGroup?.userData.isMovementMode;
            const isNotEditingMode =
                !store.getState().roomPlanner.isRoomPlannerEditing;

            // Handle E (Edit)
            if (
                (event.key === 'e' || event.key === 'E') &&
                selectedCabinetGroup &&
                editModeHandlerRef.current &&
                isNotMovementMode
            ) {
                event.preventDefault();
                editModeHandlerRef.current();
                return;
            }

            // Handle D (Duplicate)
            if (
                (event.key === 'd' || event.key === 'D') &&
                selectedCabinetGroup &&
                duplicateHandlerRef.current &&
                isNotMovementMode &&
                isNotEditingMode
            ) {
                event.preventDefault();
                duplicateHandlerRef.current();
                return;
            }

            // Handle O (Open doors/drawers)
            if (
                (event.key === 'o' || event.key === 'O') &&
                selectedCabinetGroup &&
                doorToggleHandlerRef.current &&
                isNotMovementMode &&
                isNotEditingMode
            ) {
                event.preventDefault();
                doorToggleHandlerRef.current();
                return;
            }

            // Handle R key (Rotate Right)
            if (
                (event.key === 'r' || event.key === 'R') &&
                selectedCabinetGroup &&
                rotateRightHandlerRef.current &&
                isNotMovementMode &&
                isNotEditingMode
            ) {
                event.preventDefault();
                rotateRightHandlerRef.current();
                return;
            }

            // Handle M key (Move)
            if (
                (event.key === 'm' || event.key === 'M') &&
                selectedCabinetGroup &&
                movementHandlerRef.current &&
                isNotEditingMode
            ) {
                event.preventDefault();
                movementHandlerRef.current();
                return;
            }

            // Handle Delete key (cabinets and placeholders)
            if (
                (event.key === 'Delete' || event.key === 'Backspace') &&
                selectedCabinetGroup &&
                isNotMovementMode &&
                isNotEditingMode
            ) {
                event.preventDefault();
                if (selectedCabinetGroup.userData?.isPlaceholder) {
                    confirmPlaceholderDelete({
                        group: selectedCabinetGroup,
                        placeholderListRef,
                        cabinetGroupListRef,
                        showDialog,
                        onDeleted: () => setSelectedCabinetGroup(null),
                    });
                    return;
                }
                if (deleteHandlerRef.current) {
                    deleteHandlerRef.current();
                }
                return;
            }

            // Handle Ctrl+Z (undo) or Cmd+Z (macOS)
            if (
                (event.key === 'z' || event.key === 'Z') &&
                (event.ctrlKey || event.metaKey) &&
                undoHandlerRef.current
            ) {
                event.preventDefault();
                undoHandlerRef.current();
                return;
            }

            // Handle Ctrl+Y (redo) or Cmd+Y (macOS)
            if (
                (event.key === 'y' || event.key === 'Y') &&
                (event.ctrlKey || event.metaKey) &&
                redoHandlerRef.current
            ) {
                event.preventDefault();
                redoHandlerRef.current();
                return;
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedCabinetGroup, showDialog]);

    const handleUndo = useCallback(() => {
        if (undoHandlerRef.current) {
            undoHandlerRef.current();
        }
    }, []);

    const handleRedo = useCallback(
        (
            skipHistoryIndexUpdate?: boolean,
            originIndex?: number,
            targetIndex?: number
        ) => {
            if (redoHandlerRef.current) {
                redoHandlerRef.current(
                    skipHistoryIndexUpdate,
                    originIndex,
                    targetIndex
                );
            }
        },
        []
    );

    const handleGoToHistoryIndex = useCallback((targetIndex: number) => {
        if (goToHistoryIndexRef.current) {
            goToHistoryIndexRef.current(targetIndex);
        }
    }, []);

    const confirmDiscardProductChanges = useCallback(
        (yesHandler?: () => void) => {
            closeProductEditorMenuRef.current?.(yesHandler);
        },
        []
    );

    // TODO: why is this necessary?
    const handleDelete = useCallback(() => {
        if (deleteHandlerRef.current) {
            deleteHandlerRef.current();
        }
    }, []);

    return (
        <RoomPlannerContext.Provider
            value={{
                selectedCabinetGroup,
                selectedProduct,
                setSelectedCabinetGroup,
                cabinetGroupListRef,
                placeholderListRef,
                setSelectedProduct,
                originalDepthRef,
                originalHeightRef,
                originalWidthRef,
                deletedCabinetsRef,
                setEditModeHandler,
                setDuplicateHandler,
                setDoorToggleHandler,
                setRotateRightHandler,
                setMovementHandler,
                setDeleteHandler,
                setUndoHandler,
                setRedoHandler,
                setGoToHistoryIndex,
                setCloseProductEditorMenu,
                hasUnsavedProductChanges,
                confirmDiscardProductChanges,
                undoHandler: handleUndo,
                redoHandler: handleRedo,
                goToHistoryIndex: handleGoToHistoryIndex,
                deleteHandler: handleDelete,
                activeDirection,
                setActiveDirection,
                editorMode,
                setEditorMode,
                isRoomPreview,
            }}>
            {children}
        </RoomPlannerContext.Provider>
    );
};

export {RoomPlannerContext, useRoomPlannerContext, RoomPlannerProvider};
